import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { useTheme } from "./hooks/useTheme";
import Cookies from "js-cookie";
import useTenant from "./hooks/useTenant";

interface AvailabilityInputProps {
  label: string;
  name: "vin" | "vehicleKey" | "vehicleBarcode" | "licensePlate" | "name";
  value: string;
  onChange: (value: string) => void;
  baseURL: string;
  validate?: (value: string) => string | null;
  onAvailabilityChange?: (isAvailable: boolean) => void;
  disabled?: boolean;
  isClonedData?: any | null;
}

const AvailabilityInput: React.FC<AvailabilityInputProps> = ({
  label,
  name,
  value,
  onChange,
  baseURL,
  validate,
  onAvailabilityChange,
  disabled = false,
  isClonedData = null
}) => {
  const { handleTenantError }: any = useTenant();
  const tenantId = Cookies.get("tenantId-jj-rental-ui") || "";
  const { themeStyles } = useTheme();
  const [availability, setAvailability] = useState({
    isAvailable: null as boolean | null,
    showError: false,
    showSuccess: false,
    validationError: "",
  });
  const [isCloned, setIsCloned] = useState<boolean>(true);

  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  const endpoints = {
    vin: "vehicles/DoesVinExist",
    vehicleKey: "vehicles/DoesVehicleKeyExist",
    vehicleBarcode: "vehicles/DoesVehicleBarcodeExist",
    licensePlate: "vehicles/DoesLicensePlateExist",
    name: "additionalCharge/DoesAdditionalChargeNameExist",
  };

  const checkAvailability = async (value: string) => {
    if (!value.trim()) return;
    let response: any = null;
    try {
      if (name === "name") {
        response = await axios.get<boolean>(
          `${baseURL}${endpoints[name]}?additionalChargeName=${value}`, {
          headers: {
            'X-Tenant-ID': tenantId,
          }
        })
      } else {
        response = await axios.get<boolean>(
          `${baseURL}${endpoints[name]}?${name}=${encodeURIComponent(value)}`, {
          headers: {
            'X-Tenant-ID': tenantId,
          }
        }
        )
      }

      const isExist = response.data;
      const isAvailable = !isExist;

      setAvailability((prev) => ({
        ...prev,
        isAvailable,
        showError: isExist,
        showSuccess: isAvailable,
        validationError: "",
      }));

      onAvailabilityChange?.(isAvailable);

      if (isAvailable) {
        setTimeout(() => {
          setAvailability((prev) => ({ ...prev, showSuccess: false }));
        }, 3000);
      }
      handleTenantError(null);
    } catch (error) {
      handleTenantError(error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;
    onChange(inputValue);

    setAvailability({
      isAvailable: null,
      showError: false,
      showSuccess: false,
      validationError: "",
    });

    if (validate) {
      const errorMsg = validate(inputValue);
      if (errorMsg) {
        setAvailability((prev) => ({ ...prev, validationError: errorMsg }));
        onAvailabilityChange?.(false);
        return;
      }
    }

    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      checkAvailability(inputValue);
    }, 1000);
  };

  useEffect(() => {
    if (isClonedData?.isCloned && isCloned) {
      setAvailability({
        isAvailable: false,
        showError: true,
        showSuccess: false,
        validationError: "",
      });

      onAvailabilityChange?.(false);
      if (validate) {
        const errorMsg = validate(isClonedData?.inputValue);
        if (errorMsg) {
          setAvailability((prev) => ({ ...prev, validationError: errorMsg }));
        }
      }

      setIsCloned(false);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isClonedData])

  return (
    <div>
      <label className={`text-xs font-medium ${themeStyles.form.tailwindLabelTextColor}`}>{label}</label>
      <input
        type="text"
        id={name}
        name={name}
        className={`w-full px-[8px] py-[8px] border rounded-md ${themeStyles.form.tailwindBorderColor} outline-none focus:border-[#167AC3] text-sm ${themeStyles.form.tailwindInputTextColor} bg-transparent`}
        value={value}
        onChange={handleInputChange}
        required
        disabled={disabled}
      />

      {availability.validationError && (
        <div style={{ color: "#ff3333", fontSize: "12px", marginTop: "5px" }}>
          {availability.validationError}
        </div>
      )}
      {availability.showError && (
        <div style={{ color: "#ff3333", fontSize: "12px", marginTop: "5px" }}>
          ❌ {label} already exists
        </div>
      )}
      {availability.showSuccess && (
        <div style={{ color: "#00b300", fontSize: "12px", marginTop: "5px" }}>
          ✅ {label} is available
        </div>
      )}
    </div>
  );
};

export default AvailabilityInput;